import React from 'react';
import { Typography } from 'antd';
import styled from 'styled-components';
import { formatTime } from '../../section/Common/utils/getDate';
import { ENDPOINT_TO_TAB_NAME } from '../../core/constants';
import { avatarColor, getFirstName } from '../../section/Common/utils/getUser';

const { Text } = Typography;

export interface INote {
  id: number;
  action_type: string | null;
  moved_to_endpoint: string | null;
  note: string;
  username: string;
  creation_time: string; // ISO string from DB
}

const NoteCard = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ActionLabel = styled(Text)`
  font-size: 15px;
  color: #1a1a1a;
`;

const Endpoint = styled.span`
  font-weight: 700;
  color: #1a1a1a;
`;

const Timestamp = styled(Text)`
  font-size: 12px;
  color: #8c8c8c;
  white-space: nowrap;
  margin-left: 12px;
`;

const NoteBody = styled(Text)`
  display: block;
  font-size: 14px;
  color: #3a3a3a;
  line-height: 1.6;
  margin-bottom: 12px;
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.div<{ bg: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${({ bg }) => bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
`;

const PIN_ACTION_LABELS: Record<string, string> = {
  pin_watchlist: 'Pinned to top of Watchlist',
  unpin_watchlist: 'Unpinned from Watchlist'
};

const renderAction = (note: INote) => {
  // Pin/unpin toggles are state changes, not tab movements.
  if (note.action_type && PIN_ACTION_LABELS[note.action_type]) {
    return <Endpoint>{PIN_ACTION_LABELS[note.action_type]}</Endpoint>;
  }

  if (note.moved_to_endpoint) {
    return (
      <>
        {note.action_type === 'return_from_list' ? 'Returned from ' : 'Moved to '}
        <Endpoint>
          {ENDPOINT_TO_TAB_NAME[note.moved_to_endpoint ?? ''] ?? note.moved_to_endpoint}
        </Endpoint>
      </>
    );
  }

  return <Endpoint>{note.action_type}</Endpoint>;
};

const NoteItem: React.FC<{ note: INote }> = ({ note }) => (
  <NoteCard>
    <NoteHeader>
      <ActionLabel>{renderAction(note)}</ActionLabel>
      <Timestamp>{formatTime(note.creation_time)}</Timestamp>
    </NoteHeader>

    {note.note && <NoteBody>{note.note}</NoteBody>}

    <AvatarRow>
      <Avatar bg={avatarColor(note.username)}>
        {getFirstName(note.username).charAt(0)
          .toUpperCase()}
      </Avatar>
      <Text style={{ fontSize: 14 }}>{getFirstName(note.username)}</Text>
    </AvatarRow>
  </NoteCard>
);

export default NoteItem;